import React, { useEffect, useMemo, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Popover, Select, Stack, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { applyTemplateTokens } from '../office/templateTokens';

const BORDER = '#e8e8e8';
const PANEL = '#f3f0ea';
const GOLD_D = '#7a5525';
const MUTED = '#6b6b6b';
const FAINT = '#938b7d';
const DARK = '#1a1a1a';
const GREEN = '#3c6b4b';
const WARN = '#c07a12';

type AttendanceStatus = 'present' | 'half_day' | 'absent';
type AttendanceAuditStatus = AttendanceStatus | 'not_marked';
type AbsenceReasonCode = 'without_notice' | 'approved_leave' | 'sick' | 'emergency' | 'other';
type SalaryMode = 'attendance_based' | 'fixed_monthly';

type AttendanceUpdate = {
  status: AttendanceStatus;
  absenceReasonCode?: AbsenceReasonCode;
  attendanceNote?: string;
};

type CompanyProfile = {
  id: string;
  name?: string;
  address?: string;
  mobile?: string;
  email?: string;
  logoUrl?: string;
  logoBwUrl?: string;
  fontFamily?: string;
  fontUrl?: string;
  fontFormat?: string;
  gujaratiFontFamily?: string;
  gujaratiFontUrl?: string;
  gujaratiFontFormat?: string;
};

type PayrollEmployee = {
  id: string;
  employeeName: string;
  mobile?: string;
  address?: string;
  designation?: string;
  dutyTiming?: string;
  joiningDate?: string;
  workLocation?: string;
  idProofType?: string;
  idProofNumber?: string;
  idProofFileName?: string;
  idProofUrl?: string;
  idProofPath?: string;
  companyProfileId: string;
  companyName: string;
  salaryMode?: SalaryMode;
  monthlySalary: number;
  allowedHolidayDays: number;
  tdsPercent: number;
  tdsAmount?: number;
  active: boolean;
  notes?: string;
};

type AttendanceRecord = {
  id: string;
  date: string;
  monthKey: string;
  employeeId: string;
  status: AttendanceStatus;
  absenceReasonCode?: AbsenceReasonCode;
  attendanceNote?: string;
  updatedAt?: any;
  updatedBy?: string;
};

type AttendanceHistoryRecord = {
  id: string;
  date: string;
  monthKey: string;
  employeeId: string;
  employeeName: string;
  companyProfileId: string;
  companyName: string;
  oldStatus: AttendanceAuditStatus;
  newStatus: AttendanceStatus;
  oldAbsenceReasonCode?: AbsenceReasonCode;
  newAbsenceReasonCode?: AbsenceReasonCode;
  oldAttendanceNote?: string;
  newAttendanceNote?: string;
  changedAt?: any;
  changedBy?: string;
};

type PayrollCalc = {
  salaryBaseDays: number;
  presentDays: number;
  regularPayableDays: number;
  overtimeHoldDays: number;
  perDayRate: number;
  grossSalaryPay: number;
  tdsAmount: number;
  overtimeHoldAmount: number;
  netPayableAmount: number;
};

type ProjectMeta = {
  id: string;
  name?: string;
  logoUrl?: string;
  logoBwUrl?: string;
  backgroundImageUrl?: string;
  documentImageUrl?: string;
};

const money = (value: number) => Number(Math.round(value || 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const currentMonthKey = () => new Date().toISOString().slice(0, 7);
const numberValue = (value: string) => Number(value || 0);
const DEFAULT_COMPANY_NAME = 'swarna infra';

function daysInMonth(monthKey: string) {
  const [year, month] = monthKey.split('-').map((part) => Number(part));
  if (!year || !month) return 30;
  return new Date(year, month, 0).getDate();
}

function attendanceDateKeysForMonth(monthKey: string) {
  const today = new Date();
  const todayMonthKey = today.toISOString().slice(0, 7);
  if (monthKey > todayMonthKey) return [];
  const total = monthKey === todayMonthKey ? today.getDate() : daysInMonth(monthKey);
  return Array.from({ length: total }, (_, index) => `${monthKey}-${String(index + 1).padStart(2, '0')}`);
}

function attendanceValue(status?: AttendanceStatus) {
  if (status === 'present') return 1;
  if (status === 'half_day') return 0.5;
  return 0;
}

function safeDocId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function defaultCompanyProfile(companyProfiles: CompanyProfile[]) {
  return companyProfiles.find((company) => String(company.name || '').trim().toLowerCase() === DEFAULT_COMPANY_NAME) || companyProfiles[0] || null;
}

function statusLabel(status?: AttendanceAuditStatus) {
  if (status === 'present') return 'Present';
  if (status === 'half_day') return 'Half day';
  if (status === 'not_marked') return 'Not marked';
  return 'Absent';
}

const ABSENCE_REASON_OPTIONS: Array<{ value: AbsenceReasonCode; label: string }> = [
  { value: 'without_notice', label: 'Without prior notice or approval' },
  { value: 'approved_leave', label: 'Approved leave' },
  { value: 'sick', label: 'Sick / medical reason' },
  { value: 'emergency', label: 'Personal or family emergency' },
  { value: 'other', label: 'Other' },
];

function absenceReasonLabel(reason?: AbsenceReasonCode) {
  return ABSENCE_REASON_OPTIONS.find((option) => option.value === reason)?.label || '';
}

function formatDateTime(value: any) {
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDate(value?: string) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function amountInWords(value: number) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const belowHundred = (num: number) => num < 20 ? ones[num] : `${tens[Math.floor(num / 10)]}${num % 10 ? ` ${ones[num % 10]}` : ''}`;
  const belowThousand = (num: number) => {
    const hundred = Math.floor(num / 100);
    const rest = num % 100;
    return `${hundred ? `${ones[hundred]} Hundred${rest ? ' ' : ''}` : ''}${rest ? belowHundred(rest) : ''}`.trim();
  };
  const rounded = Math.max(0, Math.round(Number(value || 0)));
  if (!rounded) return 'Zero Rupees';
  const parts: string[] = [];
  const crore = Math.floor(rounded / 10000000);
  const lakh = Math.floor((rounded % 10000000) / 100000);
  const thousand = Math.floor((rounded % 100000) / 1000);
  const rest = rounded % 1000;
  if (crore) parts.push(`${belowThousand(crore)} Crore`);
  if (lakh) parts.push(`${belowThousand(lakh)} Lakh`);
  if (thousand) parts.push(`${belowThousand(thousand)} Thousand`);
  if (rest) parts.push(belowThousand(rest));
  return `${parts.join(' ')} Rupees`;
}

function safeFileName(value: string) {
  return String(value || 'joining-letter').trim().replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'joining_letter';
}

function blobFromRenderTemplateResult(resultData: unknown): Blob {
  const data = (resultData || {}) as Record<string, any>;
  const mimeType = String(data.mimeType || 'application/pdf');
  const rawBytes = Array.isArray(data.pdfBytes)
    ? data.pdfBytes.map((item: any) => Number(item || 0))
    : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes).filter((key) => /^\d+$/.test(key)).sort((a, b) => Number(a) - Number(b)).map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : null;
  if (rawBytes && rawBytes.length) return new Blob([new Uint8Array(rawBytes)], { type: mimeType });
  const rawBase64 = typeof data.pdfBase64 === 'string'
    ? data.pdfBase64
    : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
      ? (() => {
        const source = data.pdfBase64.data || [];
        let binaryBuffer = '';
        const chunkSize = 0x8000;
        for (let index = 0; index < source.length; index += chunkSize) {
          binaryBuffer += String.fromCharCode(...source.slice(index, index + chunkSize));
        }
        return btoa(binaryBuffer);
      })()
      : '';
  const normalizedBase64 = String(rawBase64 || '').replace(/^data:application\/pdf;base64,/i, '').replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');
  if (!normalizedBase64) throw new Error('PDF response was empty.');
  const binary = atob(normalizedBase64.padEnd(normalizedBase64.length + ((4 - (normalizedBase64.length % 4)) % 4), '='));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType });
}

function calculatePayroll(employee: PayrollEmployee, monthDays: number, attendance: AttendanceRecord[]): PayrollCalc {
  const salary = Math.max(0, Number(employee.monthlySalary || 0));
  const salaryMode = employee.salaryMode || 'attendance_based';
  const presentDays = attendance.reduce((sum, record) => sum + attendanceValue(record.status), 0);
  if (salaryMode === 'fixed_monthly') {
    const tdsAmount = Math.round(salary * Math.max(0, Number(employee.tdsPercent || 0)) / 100);
    return {
      salaryBaseDays: monthDays,
      presentDays,
      regularPayableDays: monthDays,
      overtimeHoldDays: 0,
      perDayRate: Math.round(salary / Math.max(1, monthDays)),
      grossSalaryPay: Math.round(salary),
      tdsAmount,
      overtimeHoldAmount: 0,
      netPayableAmount: Math.max(0, Math.round(salary) - tdsAmount),
    };
  }
  const holidays = Math.max(0, Math.min(Number(employee.allowedHolidayDays || 0), monthDays - 1));
  const salaryBaseDays = Math.max(1, monthDays - holidays);
  const rawPerDayRate = salary / salaryBaseDays;
  const regularPayableDays = Math.min(presentDays, salaryBaseDays);
  const overtimeHoldDays = Math.max(0, presentDays - salaryBaseDays);
  const grossSalaryPay = Math.round(rawPerDayRate * regularPayableDays);
  const overtimeHoldAmount = Math.round(rawPerDayRate * overtimeHoldDays);
  const tdsAmount = Math.round(grossSalaryPay * Math.max(0, Number(employee.tdsPercent || 0)) / 100);
  return {
    salaryBaseDays,
    presentDays,
    regularPayableDays,
    overtimeHoldDays,
    perDayRate: Math.round(rawPerDayRate),
    grossSalaryPay,
    tdsAmount,
    overtimeHoldAmount,
    netPayableAmount: Math.max(0, grossSalaryPay - tdsAmount),
  };
}

export default function OfficePayrollPage() {
  const { currentProjectId, roleId, currentUser } = useAuth();
  const { canAction } = usePermissions();
  const isAdmin = ['super-admin', 'admin'].includes(String(roleId || ''));
  const canManage = isAdmin || canAction('payroll', 'manage');
  const canAudit = isAdmin || canAction('payroll', 'audit');
  const [tab, setTab] = useState<'attendance' | 'payroll' | 'employees' | 'audit'>('attendance');
  const [monthKey, setMonthKey] = useState(currentMonthKey());
  const [companyProfiles, setCompanyProfiles] = useState<CompanyProfile[]>([]);
  const [companyProfileId, setCompanyProfileId] = useState('');
  const [projectMeta, setProjectMeta] = useState<ProjectMeta | null>(null);
  const [payrollDesignationOptions, setPayrollDesignationOptions] = useState<string[]>([]);
  const [employees, setEmployees] = useState<PayrollEmployee[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceHistoryRecord[]>([]);
  const [editingEmployee, setEditingEmployee] = useState<PayrollEmployee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<PayrollEmployee | null>(null);
  const [joiningLetterEmployee, setJoiningLetterEmployee] = useState<PayrollEmployee | null>(null);
  const [letterBusyEmployeeId, setLetterBusyEmployeeId] = useState('');
  const [attendancePicker, setAttendancePicker] = useState<{ employee: PayrollEmployee; date: string; record?: AttendanceRecord; mouseX: number; mouseY: number } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => onSnapshot(query(collection(db, 'companyProfiles')), (snapshot) => {
    const rows = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<CompanyProfile, 'id'>) })).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    setCompanyProfiles(rows);
    setCompanyProfileId((previous) => previous || defaultCompanyProfile(rows)?.id || '');
  }, () => setCompanyProfiles([])), []);

  useEffect(() => {
    if (!currentProjectId) { setEmployees([]); return; }
    return onSnapshot(query(collection(db, 'projects', currentProjectId, 'payrollEmployees')), (snapshot) => {
      setEmployees(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<PayrollEmployee, 'id'>) })).sort((a, b) => String(a.employeeName || '').localeCompare(String(b.employeeName || ''))));
    }, () => setError('Could not load payroll employees.'));
  }, [currentProjectId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentProjectId) { setProjectMeta(null); return; }
      const snap = await getDoc(doc(db, 'projects', currentProjectId));
      if (cancelled) return;
      setProjectMeta(snap.exists() ? { id: snap.id, ...(snap.data() as Omit<ProjectMeta, 'id'>) } : { id: currentProjectId });
    };
    run().catch(() => !cancelled && setProjectMeta(currentProjectId ? { id: currentProjectId } : null));
    return () => { cancelled = true; };
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setPayrollDesignationOptions([]); return; }
    return onSnapshot(doc(db, 'projects', currentProjectId, 'payrollSettings', 'options'), (snapshot) => {
      const options = Array.isArray(snapshot.data()?.designationOptions) ? snapshot.data()?.designationOptions : [];
      setPayrollDesignationOptions(options.map((value: any) => String(value || '').trim()).filter(Boolean));
    }, () => setPayrollDesignationOptions([]));
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setAttendance([]); return; }
    return onSnapshot(query(collection(db, 'projects', currentProjectId, 'payrollAttendance')), (snapshot) => {
      setAttendance(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<AttendanceRecord, 'id'>) })));
    }, () => setError('Could not load attendance.'));
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId || !canAudit) { setAttendanceHistory([]); return; }
    return onSnapshot(query(collection(db, 'projects', currentProjectId, 'payrollAttendanceHistory')), (snapshot) => {
      const rows = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<AttendanceHistoryRecord, 'id'>) }));
      setAttendanceHistory(rows.sort((a, b) => {
        const aMs = a.changedAt?.toDate ? a.changedAt.toDate().getTime() : 0;
        const bMs = b.changedAt?.toDate ? b.changedAt.toDate().getTime() : 0;
        return bMs - aMs;
      }));
    }, () => setError('Could not load attendance history.'));
  }, [canAudit, currentProjectId]);

  const monthDays = useMemo(() => daysInMonth(monthKey), [monthKey]);
  const dateKeys = useMemo(() => attendanceDateKeysForMonth(monthKey), [monthKey]);
  const activeEmployees = useMemo(() => employees.filter((employee) => employee.active !== false && (!companyProfileId || employee.companyProfileId === companyProfileId)), [companyProfileId, employees]);
  const designationOptions = useMemo(() => Array.from(new Set([
    'Accountant',
    'Site Supervisor',
    'Engineer',
    'Sales Executive',
    'Office Staff',
    'Driver',
    'Security',
    'Housekeeping',
    ...payrollDesignationOptions,
    ...employees.map((employee) => String(employee.designation || '').trim()).filter(Boolean),
  ])).sort((a, b) => a.localeCompare(b)), [employees, payrollDesignationOptions]);
  const attendanceByEmployeeDate = useMemo(() => {
    const map = new Map<string, AttendanceRecord>();
    attendance.filter((record) => record.monthKey === monthKey).forEach((record) => map.set(`${record.employeeId}_${record.date}`, record));
    return map;
  }, [attendance, monthKey]);

  const payrollRows = useMemo(() => activeEmployees.map((employee) => {
    const employeeAttendance = attendance.filter((record) => record.monthKey === monthKey && record.employeeId === employee.id);
    return { employee, calc: calculatePayroll(employee, monthDays, employeeAttendance) };
  }), [activeEmployees, attendance, monthDays, monthKey]);

  const totals = useMemo(() => payrollRows.reduce((sum, row) => ({
    grossSalaryPay: sum.grossSalaryPay + row.calc.grossSalaryPay,
    tdsAmount: sum.tdsAmount + row.calc.tdsAmount,
    overtimeHoldAmount: sum.overtimeHoldAmount + row.calc.overtimeHoldAmount,
    netPayableAmount: sum.netPayableAmount + row.calc.netPayableAmount,
  }), { grossSalaryPay: 0, tdsAmount: 0, overtimeHoldAmount: 0, netPayableAmount: 0 }), [payrollRows]);

  const saveEmployee = async (employee: PayrollEmployee) => {
    if (!currentProjectId) return;
    setError('');
    try {
      const selectedCompany = companyProfiles.find((company) => company.id === employee.companyProfileId);
      const payload = {
        employeeName: employee.employeeName.trim(),
        mobile: String(employee.mobile || '').trim(),
        companyProfileId: employee.companyProfileId,
        companyName: selectedCompany?.name || employee.companyName || '',
        salaryMode: employee.salaryMode || 'attendance_based',
        monthlySalary: Math.round(Number(employee.monthlySalary || 0)),
        allowedHolidayDays: Math.round(Number(employee.allowedHolidayDays || 0)),
        tdsPercent: Number(employee.tdsPercent || 0),
        active: employee.active !== false,
        notes: String(employee.notes || '').trim(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || currentUser?.uid || '',
      };
      if (employee.id) {
        await updateDoc(doc(db, 'projects', currentProjectId, 'payrollEmployees', employee.id), payload);
      } else {
        await addDoc(collection(db, 'projects', currentProjectId, 'payrollEmployees'), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.email || currentUser?.uid || '',
        });
      }
      setEditingEmployee(null);
    } catch (err: any) {
      setError(err?.message || 'Could not save employee.');
    }
  };

  const deleteEmployee = async (employee: PayrollEmployee, includeRelatedData: boolean) => {
    if (!currentProjectId || !canManage) return;
    setError('');
    try {
      const refsToDelete = [doc(db, 'projects', currentProjectId, 'payrollEmployees', employee.id)];
      if (includeRelatedData) {
        const attendanceSnap = await getDocs(query(collection(db, 'projects', currentProjectId, 'payrollAttendance'), where('employeeId', '==', employee.id)));
        const historySnap = await getDocs(query(collection(db, 'projects', currentProjectId, 'payrollAttendanceHistory'), where('employeeId', '==', employee.id)));
        attendanceSnap.docs.forEach((item) => refsToDelete.push(item.ref));
        historySnap.docs.forEach((item) => refsToDelete.push(item.ref));
      }
      for (let i = 0; i < refsToDelete.length; i += 450) {
        const batch = writeBatch(db);
        refsToDelete.slice(i, i + 450).forEach((ref) => batch.delete(ref));
        await batch.commit();
      }
      setDeletingEmployee(null);
    } catch (err: any) {
      setError(err?.message || 'Could not delete employee.');
    }
  };

  const markAttendance = async (employee: PayrollEmployee, date: string, update: AttendanceUpdate, existingRecord?: AttendanceRecord) => {
    if (!currentProjectId || !canManage) return;
    const status = update.status;
    const oldStatus: AttendanceAuditStatus = existingRecord?.status || 'not_marked';
    const oldAbsenceReasonCode = existingRecord?.absenceReasonCode;
    const oldAttendanceNote = String(existingRecord?.attendanceNote || '').trim();
    const newAbsenceReasonCode = status === 'absent' ? update.absenceReasonCode : undefined;
    const newAttendanceNote = status === 'absent' ? String(update.attendanceNote || '').trim() : '';
    if (existingRecord && oldStatus === status && oldAbsenceReasonCode === newAbsenceReasonCode && oldAttendanceNote === newAttendanceNote) {
      setAttendancePicker(null);
      return;
    }
    setError('');
    const id = safeDocId(`${monthKey}_${employee.id}_${date}`);
    try {
      const batch = writeBatch(db);
      const attendanceRef = doc(db, 'projects', currentProjectId, 'payrollAttendance', id);
      const historyRef = doc(collection(db, 'projects', currentProjectId, 'payrollAttendanceHistory'));
      const changedBy = currentUser?.email || currentUser?.uid || '';
      batch.set(attendanceRef, {
        date,
        monthKey,
        employeeId: employee.id,
        employeeName: employee.employeeName,
        companyProfileId: employee.companyProfileId,
        companyName: employee.companyName,
        status,
        absenceReasonCode: newAbsenceReasonCode || '',
        attendanceNote: newAttendanceNote,
        updatedAt: serverTimestamp(),
        updatedBy: changedBy,
      }, { merge: true });
      batch.set(historyRef, {
        attendanceId: id,
        date,
        monthKey,
        employeeId: employee.id,
        employeeName: employee.employeeName,
        companyProfileId: employee.companyProfileId,
        companyName: employee.companyName,
        oldStatus,
        newStatus: status,
        oldAbsenceReasonCode: oldAbsenceReasonCode || '',
        newAbsenceReasonCode: newAbsenceReasonCode || '',
        oldAttendanceNote,
        newAttendanceNote,
        changedAt: serverTimestamp(),
        changedBy,
      });
      await batch.commit();
      setAttendancePicker(null);
    } catch (err: any) {
      setError(err?.message || 'Could not save attendance.');
    }
  };

  const generateJoiningLetter = async (employee: PayrollEmployee) => {
    if (!currentProjectId) return;
    setError('');
    setLetterBusyEmployeeId(employee.id);
    try {
      const templatesSnap = await getDocs(collection(db, 'officeDocumentTemplates'));
      const candidateTemplates = templatesSnap.docs
        .map((item) => ({ id: item.id, ...(item.data() as any) }))
        .filter((template) => {
          const templateId = String(template.templateId || '').trim();
          const groupKey = String(template.documentGroupKey || template.visualModel?.documentGroupKey || '').trim();
          return templateId === 'joining_letter_v1' || templateId === 'employee_joining_letter_v1' || groupKey === 'joining_letter';
        });
      const assignedTemplates = candidateTemplates.filter((template) => {
        const assigned = Array.isArray(template.assignedProjectIds) ? template.assignedProjectIds.map((value: any) => String(value)) : [];
        return assigned.includes(String(currentProjectId));
      });
      const toMillis = (value: any) => typeof value?.toMillis === 'function' ? value.toMillis() : typeof value?.seconds === 'number' ? value.seconds * 1000 : 0;
      const pickNewest = (rows: any[]) => [...rows].sort((a, b) => {
        const versionDiff = (Number(b.version || 0) || 0) - (Number(a.version || 0) || 0);
        if (versionDiff) return versionDiff;
        return toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt);
      })[0] || null;
      const selectedTemplate = pickNewest(assignedTemplates) || pickNewest(candidateTemplates);
      if (!selectedTemplate) {
        setError('Create a Document Template named "Joining Letter" from Settings first.');
        return;
      }
      const selectedCompany = companyProfiles.find((company) => company.id === employee.companyProfileId) || defaultCompanyProfile(companyProfiles);
      const salaryModeLabel = (employee.salaryMode || 'attendance_based') === 'fixed_monthly'
        ? 'Fixed monthly salary'
        : 'Attendance based salary';
      const issueDate = formatDate(new Date().toISOString().slice(0, 10));
      const tokenValues: Record<string, string> = {
        '{{issue_date}}': issueDate,
        '{{ref_no}}': `HR/${new Date().getFullYear()}/${employee.id.slice(0, 6).toUpperCase()}`,
        '{{company_name}}': String(selectedCompany?.name || employee.companyName || ''),
        '{{company_address}}': String(selectedCompany?.address || ''),
        '{{company_mobile}}': String(selectedCompany?.mobile || ''),
        '{{company_email}}': String(selectedCompany?.email || ''),
        '{{company_logo_url}}': String(selectedCompany?.logoUrl || ''),
        '{{project_logo_url}}': String(projectMeta?.logoUrl || ''),
        '{{project_document_image_url}}': String(projectMeta?.documentImageUrl || ''),
        '{{project_name}}': String(projectMeta?.name || currentProjectId || ''),
        '{{employee_name}}': String(employee.employeeName || ''),
        '{{employee_mobile}}': String(employee.mobile || ''),
        '{{employee_address}}': String(employee.address || ''),
        '{{employee_designation}}': String(employee.designation || 'Employee'),
        '{{employee_duty_timing}}': String(employee.dutyTiming || ''),
        '{{employee_joining_date}}': formatDate(employee.joiningDate) || '',
        '{{employee_salary}}': money(employee.monthlySalary),
        '{{employee_salary_words}}': amountInWords(employee.monthlySalary),
        '{{employee_company_name}}': String(selectedCompany?.name || employee.companyName || ''),
        '{{employee_project_name}}': String(projectMeta?.name || currentProjectId || ''),
        '{{employee_work_location}}': String(employee.workLocation || projectMeta?.name || 'Office / Project Site'),
        '{{employee_salary_mode}}': salaryModeLabel,
        '{{employee_allowed_holidays}}': String(Number(employee.allowedHolidayDays || 0)),
        '{{employee_tds_percent}}': String(Number(employee.tdsPercent || 0)),
      };
      const templateHtml = String(
        selectedTemplate.renderHtml ||
        selectedTemplate.visualModel?.renderHtml ||
        selectedTemplate.visualModel?.body ||
        selectedTemplate.bodyHtml ||
        ''
      ).trim();
      if (!templateHtml) {
        setError('Joining Letter template has no content.');
        return;
      }
      const renderedHtml = applyTemplateTokens(templateHtml, tokenValues);
      const vm = selectedTemplate.visualModel || {};
      const prefersBwLogo = Boolean(selectedTemplate.useProjectLogoBw || vm.useProjectLogoBw);
      const companyLogoUrl = String((prefersBwLogo && selectedCompany?.logoBwUrl) || selectedCompany?.logoUrl || '');
      const projectLogoUrl = String((prefersBwLogo && projectMeta?.logoBwUrl) || projectMeta?.logoUrl || '');
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const fileBase = `${safeFileName(employee.employeeName)}_joining_letter`;
      const result = await renderTemplatePdf(buildPdfPayloadFromTemplate({
        html: renderedHtml,
        tokenValues,
        visualModel: {
          ...vm,
          refNo: tokenValues['{{ref_no}}'],
          issueDate,
          backgroundImageUrl: String(vm.backgroundImageUrl || projectMeta?.backgroundImageUrl || ''),
        },
        companyLogoUrl,
        projectLogoUrl,
        companyAddress: String(selectedCompany?.address || ''),
        companyMobile: String(selectedCompany?.mobile || ''),
        companyEmail: String(selectedCompany?.email || ''),
        companyFontFamily: String(selectedCompany?.fontFamily || ''),
        companyFontUrl: String(selectedCompany?.fontUrl || ''),
        companyFontFormat: String(selectedCompany?.fontFormat || ''),
        gujaratiFontFamily: String(selectedCompany?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(selectedCompany?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(selectedCompany?.gujaratiFontFormat || ''),
        paperSize: String(vm.paperSize || 'A4'),
        fileName: fileBase,
      }));
      const blob = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileBase}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Could not generate joining letter.');
    } finally {
      setLetterBusyEmployeeId('');
    }
  };

  const saveJoiningLetterDetails = async (employee: PayrollEmployee, draft: Partial<PayrollEmployee>, idProofFile: File | null, generateAfterSave: boolean) => {
    if (!currentProjectId || !canManage) return;
    setError('');
    setLetterBusyEmployeeId(employee.id);
    try {
      const payload: Partial<PayrollEmployee> & Record<string, any> = {
        joiningDate: String(draft.joiningDate || '').trim(),
        designation: String(draft.designation || '').trim(),
        dutyTiming: String(draft.dutyTiming || '').trim(),
        workLocation: String(draft.workLocation || '').trim(),
        address: String(draft.address || '').trim(),
        idProofType: String(draft.idProofType || '').trim(),
        idProofNumber: String(draft.idProofNumber || '').trim(),
        joiningLetterUpdatedAt: serverTimestamp(),
        joiningLetterUpdatedBy: currentUser?.email || currentUser?.uid || '',
      };
      if (idProofFile) {
        const storagePath = `projects/${currentProjectId}/payrollEmployees/${employee.id}/idProof/${Date.now()}_${safeFileName(idProofFile.name)}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, idProofFile, { contentType: idProofFile.type || 'application/octet-stream' });
        payload.idProofFileName = idProofFile.name;
        payload.idProofPath = storagePath;
        payload.idProofUrl = await getDownloadURL(storageRef);
      }
      await updateDoc(doc(db, 'projects', currentProjectId, 'payrollEmployees', employee.id), payload);
      const designation = String(payload.designation || '').trim();
      if (designation && !designationOptions.map((value) => value.toLowerCase()).includes(designation.toLowerCase())) {
        await setDoc(doc(db, 'projects', currentProjectId, 'payrollSettings', 'options'), {
          designationOptions: [...designationOptions, designation].sort((a, b) => a.localeCompare(b)),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || currentUser?.uid || '',
        }, { merge: true });
      }
      const updatedEmployee = { ...employee, ...payload } as PayrollEmployee;
      setJoiningLetterEmployee(null);
      if (generateAfterSave) await generateJoiningLetter(updatedEmployee);
    } catch (err: any) {
      setError(err?.message || 'Could not save joining letter details.');
    } finally {
      setLetterBusyEmployeeId('');
    }
  };

  const emptyEmployee = (): PayrollEmployee => ({
    id: '',
    employeeName: '',
    mobile: '',
    companyProfileId: companyProfileId || defaultCompanyProfile(companyProfiles)?.id || '',
    companyName: companyProfiles.find((company) => company.id === companyProfileId)?.name || defaultCompanyProfile(companyProfiles)?.name || '',
    salaryMode: 'attendance_based',
    monthlySalary: 0,
    allowedHolidayDays: 2,
    tdsPercent: 0,
    active: true,
    notes: '',
  });

  return <Box sx={{ maxWidth: 1240, mx: 'auto', width: '100%', px: { xs: 1, md: 3 }, pt: { xs: 1.1, md: 3 }, pb: { xs: 10, md: 3 } }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0.9, sm: 1.25 }} alignItems={{ sm: 'center' }} sx={{ mb: { xs: 1, sm: 1.5 } }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: { xs: 21, sm: 23 }, fontWeight: 900, color: DARK, lineHeight: 1.12 }}>Payroll</Typography>
        <Typography sx={{ color: MUTED, fontSize: { xs: 12, sm: 13 }, lineHeight: 1.35 }}>Employee master, daily full/half-day attendance, and monthly rounded payable calculation.</Typography>
      </Box>
      <Stack direction="row" gap={0.7} flexWrap="wrap" justifyContent={{ sm: 'flex-end' }} sx={{ '& .MuiChip-root': { height: { xs: 28, sm: 32 }, fontSize: { xs: 12, sm: 13 } } }}>
        <Chip icon={<PaymentsOutlinedIcon />} label={`Payable ₹${money(totals.netPayableAmount)}`} sx={{ bgcolor: '#eef7f0', color: GREEN, fontWeight: 800, '& .MuiChip-icon': { fontSize: { xs: 18, sm: 22 } } }} />
        <Chip label={`Held ₹${money(totals.overtimeHoldAmount)}`} sx={{ bgcolor: '#fff4e5', color: WARN, fontWeight: 800 }} />
        {canManage && <Button variant="contained" size="small" startIcon={<AddOutlinedIcon />} disabled={!currentProjectId} onClick={() => setEditingEmployee(emptyEmployee())}>Add employee</Button>}
      </Stack>
    </Stack>

    {error && <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert>}
    {!currentProjectId && <Alert severity="info" sx={{ mb: 1.2 }}>Select a project to view payroll.</Alert>}

    <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 0.75, md: 1 }} sx={{ mb: { xs: 1.05, md: 1.35 } }}>
      <Box sx={{ display: { xs: 'grid', md: 'contents' }, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '190px minmax(0, 1fr)' }, gap: { xs: 0.75, md: 1 } }}>
      <TextField label="Payroll month" type="month" value={monthKey} onChange={(event) => setMonthKey(event.target.value)} InputLabelProps={{ shrink: true }} size="small" sx={{ width: { xs: '100%', md: 190 }, '& .MuiInputBase-input': { py: { xs: 1, md: 1 } } }} />
      <FormControl size="small" sx={{ minWidth: { xs: 0, md: 230 }, width: { xs: '100%', md: 'auto' } }}>
        <InputLabel>Company</InputLabel>
        <Select label="Company" value={companyProfileId} onChange={(event) => setCompanyProfileId(event.target.value)}>
          {companyProfiles.map((company) => <MenuItem key={company.id} value={company.id}>{company.name || company.id}</MenuItem>)}
        </Select>
      </FormControl>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: 0.65, md: 1 }, flex: 1 }}>
        <SummaryMetric label="Gross" value={`₹${money(totals.grossSalaryPay)}`} />
        <SummaryMetric label="TDS" value={`₹${money(totals.tdsAmount)}`} color={WARN} />
        <SummaryMetric label="Held Overtime" value={`₹${money(totals.overtimeHoldAmount)}`} color={WARN} />
        <SummaryMetric label="Net Payable" value={`₹${money(totals.netPayableAmount)}`} color={GREEN} />
      </Box>
    </Stack>

    <Tabs
      value={tab}
      onChange={(_, value) => setTab(value)}
      variant="scrollable"
      scrollButtons={false}
      allowScrollButtonsMobile
      sx={{
        mb: { xs: 0.9, md: 1.25 },
        mx: { xs: -1, md: 0 },
        px: { xs: 1, md: 0 },
        borderBottom: `1px solid ${BORDER}`,
        '& .MuiTabs-scroller': { overflowX: 'auto !important' },
        '& .MuiTabs-flexContainer': { gap: { xs: 0.3, sm: 0.8 } },
        '& .MuiTab-root': {
          minWidth: { xs: 'auto', sm: 90 },
          px: { xs: 1.2, sm: 2 },
          minHeight: { xs: 42, sm: 48 },
          fontSize: { xs: 12.5, sm: 14 },
          fontWeight: 850,
          textTransform: 'none',
          whiteSpace: 'nowrap',
        },
      }}
    >
      <Tab value="attendance" label="Attendance" />
      <Tab value="payroll" label="Monthly Payroll" />
      <Tab value="employees" label="Employee Master" />
      {canAudit && <Tab value="audit" label="Audit Log" />}
    </Tabs>

    {tab === 'attendance' && <AttendanceGrid employees={activeEmployees} dateKeys={dateKeys} attendanceByEmployeeDate={attendanceByEmployeeDate} canManage={canManage} canViewDetails={canManage || canAudit} onOpenPicker={setAttendancePicker} />}
    {tab === 'payroll' && <PayrollSummary rows={payrollRows} />}
    {tab === 'employees' && <EmployeeMaster employees={activeEmployees} canManage={canManage} letterBusyEmployeeId={letterBusyEmployeeId} onEdit={setEditingEmployee} onDelete={setDeletingEmployee} onGenerateLetter={setJoiningLetterEmployee} />}
    {tab === 'audit' && canAudit && <AttendanceAuditLog rows={attendanceHistory.filter((row) => row.monthKey === monthKey && (!companyProfileId || row.companyProfileId === companyProfileId))} />}

    {editingEmployee && <EmployeeDialog employee={editingEmployee} companyProfiles={companyProfiles} onClose={() => setEditingEmployee(null)} onSave={saveEmployee} />}
    {deletingEmployee && <DeleteEmployeeDialog employee={deletingEmployee} onClose={() => setDeletingEmployee(null)} onDelete={deleteEmployee} />}
    {joiningLetterEmployee && <JoiningLetterDialog employee={joiningLetterEmployee} projectName={projectMeta?.name || ''} designationOptions={designationOptions} busy={letterBusyEmployeeId === joiningLetterEmployee.id} onClose={() => setJoiningLetterEmployee(null)} onSave={saveJoiningLetterDetails} />}
    {attendancePicker && <AttendanceStatusPopover selection={attendancePicker} onClose={() => setAttendancePicker(null)} onSelect={(update) => markAttendance(attendancePicker.employee, attendancePicker.date, update, attendancePicker.record)} />}
  </Box>;
}

function SummaryMetric({ label, value, color = DARK }: { label: string; value: string; color?: string }) {
  return <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#fff', px: { xs: 0.85, sm: 1.2 }, py: { xs: 0.6, sm: 0.8 }, minWidth: 0 }}>
    <Typography sx={{ color: FAINT, fontWeight: 900, fontSize: { xs: 8.5, sm: 9.5 }, textTransform: 'uppercase', lineHeight: 1.2 }}>{label}</Typography>
    <Typography sx={{ color, fontWeight: 900, fontSize: { xs: 14, sm: 15 }, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Typography>
  </Box>;
}

function Metric({ label, value, color = DARK }: { label: string; value: string; color?: string }) {
  return <Box sx={{ minWidth: 0, display: { xs: 'flex', sm: 'block' }, alignItems: { xs: 'center' }, justifyContent: { xs: 'space-between' }, gap: { xs: 1 }, py: { xs: 0.52, sm: 0 }, borderBottom: { xs: `1px solid ${BORDER}`, sm: 0 }, '&:last-of-type': { borderBottom: 0 } }}>
    <Typography sx={{ color: FAINT, fontWeight: 900, fontSize: { xs: 8.5, sm: 9.5 }, textTransform: 'uppercase', lineHeight: 1.2 }}>{label}</Typography>
    <Typography sx={{ color, fontWeight: 900, fontSize: { xs: 13, sm: 12 }, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: { xs: 'right', sm: 'left' } }}>{value}</Typography>
  </Box>;
}

function AttendanceGrid({ employees, dateKeys, attendanceByEmployeeDate, canManage, canViewDetails, onOpenPicker }: { employees: PayrollEmployee[]; dateKeys: string[]; attendanceByEmployeeDate: Map<string, AttendanceRecord>; canManage: boolean; canViewDetails: boolean; onOpenPicker: (selection: { employee: PayrollEmployee; date: string; record?: AttendanceRecord; mouseX: number; mouseY: number }) => void }) {
  const statusText = (status?: AttendanceStatus) => status === 'present' ? 'P' : status === 'half_day' ? 'H' : status === 'absent' ? 'A' : '';
  const statusColor = (status?: AttendanceStatus) => status === 'present' ? GREEN : status === 'half_day' ? WARN : MUTED;
  const longestEmployeeLabel = employees.reduce((max, employee) => Math.max(max, String(employee.employeeName || '').length), 'Employee'.length);
  const employeeColumnWidth = `${Math.min(Math.max(longestEmployeeLabel + 3, 11), 26)}ch`;
  const gridTemplateColumns = `${employeeColumnWidth} repeat(${dateKeys.length}, 42px)`;
  return <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'auto', bgcolor: '#fff' }}>
    <Box sx={{ width: 'max-content', minWidth: '100%' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ px: 1, minHeight: 48, display: 'flex', alignItems: 'center', fontWeight: 900, color: DARK, position: 'sticky', left: 0, bgcolor: PANEL, zIndex: 3, borderRight: `1px solid ${BORDER}`, whiteSpace: 'nowrap', boxShadow: '4px 0 8px rgba(0,0,0,0.04)' }}>Employee</Box>
        {dateKeys.map((date) => <Box key={date} sx={{ minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: 11, fontWeight: 900, bgcolor: PANEL, borderRight: `1px solid ${BORDER}` }}>{Number(date.slice(-2))}</Box>)}
      </Box>
      {employees.map((employee, rowIndex) => {
        const rowBg = rowIndex % 2 === 0 ? '#fff' : '#fcfbf8';
        return <Box key={employee.id} sx={{ display: 'grid', gridTemplateColumns, borderBottom: `1px solid ${BORDER}`, bgcolor: rowBg, '&:hover > *': { bgcolor: '#fffaf0' } }}>
        <Box sx={{ px: 1, py: 0.85, position: 'sticky', left: 0, bgcolor: rowBg, zIndex: 2, borderRight: `1px solid ${BORDER}`, whiteSpace: 'nowrap', boxShadow: '4px 0 8px rgba(0,0,0,0.04)' }}>
          <Typography sx={{ fontWeight: 900, color: DARK, fontSize: 13, lineHeight: 1.15 }}>{employee.employeeName}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 11 }}>₹{money(employee.monthlySalary)}</Typography>
        </Box>
        {dateKeys.map((date) => {
          const record = attendanceByEmployeeDate.get(`${employee.id}_${date}`);
          const status = record?.status;
          return <Box key={date} onClick={(event) => canManage && onOpenPicker({ employee, date, record, mouseX: event.clientX + 2, mouseY: event.clientY - 6 })} sx={{ minHeight: 46, p: 0.45, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: canManage ? 'pointer' : 'default', bgcolor: rowBg, borderRight: `1px solid ${BORDER}` }}>
            <Box title={canViewDetails && status === 'absent' ? [absenceReasonLabel(record?.absenceReasonCode), record?.attendanceNote].filter(Boolean).join(': ') || 'Absent' : undefined} sx={{ width: 27, height: 27, position: 'relative', borderRadius: 1, border: `1px solid ${status ? statusColor(status) : 'transparent'}`, color: statusColor(status), bgcolor: status === 'present' ? '#eef7f0' : status === 'half_day' ? '#fff4e5' : status === 'absent' ? '#fff' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900 }}>
              {statusText(status)}
              {canViewDetails && status === 'absent' && (record?.absenceReasonCode || record?.attendanceNote) && <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 6, height: 6, borderRadius: '50%', bgcolor: GOLD_D, border: '1px solid #fff' }} />}
            </Box>
          </Box>;
        })}
      </Box>;
      })}
      {!employees.length && <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>No employees for this company.</Box>}
      {employees.length > 0 && !dateKeys.length && <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>Attendance can be marked only after the date arrives.</Box>}
    </Box>
  </Box>;
}

function AttendanceStatusPopover({ selection, onClose, onSelect }: { selection: { employee: PayrollEmployee; date: string; record?: AttendanceRecord; mouseX: number; mouseY: number }; onClose: () => void; onSelect: (update: AttendanceUpdate) => void }) {
  const currentStatus = selection.record?.status;
  const [showAbsenceDetails, setShowAbsenceDetails] = useState(false);
  const [absenceReasonCode, setAbsenceReasonCode] = useState<AbsenceReasonCode | ''>(selection.record?.absenceReasonCode || '');
  const [attendanceNote, setAttendanceNote] = useState(selection.record?.attendanceNote || '');
  const allOptions: Array<{ status: AttendanceStatus; label: string; color: string }> = [
    { status: 'present', label: 'Present', color: GREEN },
    { status: 'half_day', label: 'Half day', color: WARN },
    { status: 'absent', label: 'Absent', color: MUTED },
  ];
  const options = allOptions.filter((option) => !currentStatus || option.status !== currentStatus);
  return <Popover
    open
    onClose={onClose}
    anchorReference="anchorPosition"
    anchorPosition={{ top: selection.mouseY, left: selection.mouseX }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    PaperProps={{ sx: { borderRadius: 1.25, border: `1px solid ${BORDER}`, boxShadow: '0 8px 22px rgba(0,0,0,0.16)', p: 0.35, width: showAbsenceDetails ? 310 : 176, maxWidth: 'calc(100vw - 24px)' } }}
  >
    {showAbsenceDetails ? <Stack gap={0.8} sx={{ p: 0.65 }}>
      <Box>
        <Typography sx={{ color: DARK, fontWeight: 900, fontSize: 13 }}>Record absence</Typography>
        <Typography sx={{ color: FAINT, fontSize: 10.5 }}>{selection.employee.employeeName} · {formatDate(selection.date)}</Typography>
      </Box>
      <FormControl size="small" fullWidth required>
        <InputLabel>Reason</InputLabel>
        <Select label="Reason" value={absenceReasonCode} onChange={(event) => setAbsenceReasonCode(event.target.value as AbsenceReasonCode)}>
          {ABSENCE_REASON_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Note (optional)"
        value={attendanceNote}
        onChange={(event) => setAttendanceNote(event.target.value)}
        multiline
        minRows={2}
        inputProps={{ maxLength: 300 }}
        placeholder="Add brief details"
      />
      <Stack direction="row" justifyContent="flex-end" gap={0.5}>
        <Button size="small" onClick={() => setShowAbsenceDetails(false)}>Back</Button>
        <Button size="small" variant="contained" disabled={!absenceReasonCode} onClick={() => onSelect({ status: 'absent', absenceReasonCode: absenceReasonCode || undefined, attendanceNote })}>Save</Button>
      </Stack>
    </Stack> : <Stack gap={0.2}>
      {currentStatus === 'absent' && <Box sx={{ px: 1, pt: 0.65, pb: 0.35 }}>
        <Typography sx={{ color: MUTED, fontWeight: 800, fontSize: 10.5 }}>{absenceReasonLabel(selection.record?.absenceReasonCode) || 'Reason not recorded'}</Typography>
        {selection.record?.attendanceNote && <Typography sx={{ color: FAINT, fontSize: 10.5, mt: 0.15, overflowWrap: 'anywhere' }}>{selection.record.attendanceNote}</Typography>}
      </Box>}
      {currentStatus === 'absent' && <Box onClick={() => setShowAbsenceDetails(true)} sx={{ px: 1, py: 0.65, borderRadius: 1, color: GOLD_D, fontWeight: 900, fontSize: 12.5, lineHeight: 1.15, cursor: 'pointer', '&:hover': { bgcolor: '#f7f3ec' } }}>
        Edit absence details
      </Box>}
      {options.map((option) => <Box key={option.status} onClick={() => option.status === 'absent' ? setShowAbsenceDetails(true) : onSelect({ status: option.status })} sx={{ px: 1, py: 0.65, borderRadius: 1, color: option.color, fontWeight: 900, fontSize: 13, lineHeight: 1.15, cursor: 'pointer', '&:hover': { bgcolor: '#f7f3ec' } }}>
        {option.label}
      </Box>)}
    </Stack>}
  </Popover>;
}

function AttendanceAuditLog({ rows }: { rows: AttendanceHistoryRecord[] }) {
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const visibleRows = useMemo(() => {
    const employeeNeedle = employeeFilter.trim().toLowerCase();
    return rows.filter((row) => {
      if (employeeNeedle && !String(row.employeeName || '').toLowerCase().includes(employeeNeedle)) return false;
      if (dateFilter && row.date !== dateFilter) return false;
      return true;
    });
  }, [dateFilter, employeeFilter, rows]);
  return <Stack gap={0.8}>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mb: 0.4 }}>
      <TextField size="small" label="Employee name" value={employeeFilter} onChange={(event) => setEmployeeFilter(event.target.value)} sx={{ width: { xs: '100%', sm: 280 } }} />
      <TextField size="small" label="Exact date" type="date" value={dateFilter} onChange={(event) => setDateFilter(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 190 } }} />
      {(employeeFilter || dateFilter) && <Button size="small" onClick={() => { setEmployeeFilter(''); setDateFilter(''); }}>Clear</Button>}
    </Stack>
    {visibleRows.map((row) => {
      const reasonChanged = row.oldAbsenceReasonCode !== row.newAbsenceReasonCode;
      const noteChanged = String(row.oldAttendanceNote || '') !== String(row.newAttendanceNote || '');
      const oldReason = absenceReasonLabel(row.oldAbsenceReasonCode) || (row.oldStatus === 'absent' ? 'Not recorded' : 'None');
      const newReason = absenceReasonLabel(row.newAbsenceReasonCode) || (row.newStatus === 'absent' ? 'Not recorded' : 'None');
      return <Box key={row.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#fff', p: { xs: 1.1, sm: 1.35 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: DARK, fontWeight: 900 }}>{row.employeeName}</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12 }}>{row.companyName} · {row.date}</Typography>
            {reasonChanged && <Typography sx={{ color: MUTED, fontSize: 11.5, mt: 0.35 }}><strong>Reason:</strong> {oldReason} → {newReason}</Typography>}
            {noteChanged && <Typography sx={{ color: MUTED, fontSize: 11.5, mt: 0.15, overflowWrap: 'anywhere' }}><strong>Note:</strong> {row.oldAttendanceNote || 'None'} → {row.newAttendanceNote || 'None'}</Typography>}
          </Box>
          <Chip size="small" label={`${statusLabel(row.oldStatus)} → ${statusLabel(row.newStatus)}`} sx={{ bgcolor: '#fff4e5', color: WARN, fontWeight: 900 }} />
          <Box sx={{ minWidth: { md: 260 } }}>
            <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 800 }}>{row.changedBy || '-'}</Typography>
            <Typography sx={{ color: FAINT, fontSize: 11.5 }}>{formatDateTime(row.changedAt)}</Typography>
          </Box>
        </Stack>
      </Box>;
    })}
    {!visibleRows.length && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 4, textAlign: 'center', color: MUTED }}>No attendance changes found for this month, company, and filters.</Box>}
  </Stack>;
}

function PayrollSummary({ rows }: { rows: Array<{ employee: PayrollEmployee; calc: PayrollCalc }> }) {
  return <Stack gap={{ xs: 0.75, sm: 1 }}>
    {rows.map(({ employee, calc }) => <Box key={employee.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: { xs: 1.25, sm: 1.5 }, bgcolor: '#fff', p: { xs: 0, sm: 1.45 }, overflow: 'hidden', boxShadow: { xs: '0 1px 5px rgba(0,0,0,0.04)', sm: 'none' } }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={{ xs: 0, sm: 1.15 }} alignItems={{ lg: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1, sm: 0 }, pb: { xs: 0.85, sm: 0 }, bgcolor: { xs: '#fff', sm: 'transparent' } }}>
          <Stack direction="row" gap={1} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: DARK, fontWeight: 900, fontSize: { xs: 15.5, sm: 17 }, lineHeight: 1.15 }}>{employee.employeeName}</Typography>
              <Typography sx={{ color: MUTED, fontSize: { xs: 10.8, sm: 12 }, lineHeight: 1.3, mt: 0.25, display: { xs: '-webkit-box', sm: 'block' }, WebkitLineClamp: { xs: 2, sm: 'unset' }, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{employee.companyName} · ₹{money(employee.monthlySalary)} salary · TDS {Number(employee.tdsPercent || 0)}% · {employee.salaryMode === 'fixed_monthly' ? 'Fixed monthly' : 'Attendance based'}</Typography>
            </Box>
            <Box sx={{ display: { xs: 'block', sm: 'none' }, flexShrink: 0, textAlign: 'right', bgcolor: '#eef7f0', borderRadius: 1, px: 0.75, py: 0.45 }}>
              <Typography sx={{ color: GREEN, fontWeight: 900, fontSize: 7.5, textTransform: 'uppercase', lineHeight: 1.1 }}>Payable</Typography>
              <Typography sx={{ color: GREEN, fontWeight: 950, fontSize: 15, lineHeight: 1.1 }}>₹{money(calc.netPayableAmount)}</Typography>
            </Box>
          </Stack>
          <Box sx={{ display: { xs: 'grid', sm: 'none' }, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.35, mt: 0.75 }}>
            <TinyStat label="Base" value={money(calc.salaryBaseDays)} />
            <TinyStat label="Present" value={money(calc.presentDays)} />
            <TinyStat label="Pay" value={money(calc.regularPayableDays)} />
            <TinyStat label="Held" value={money(calc.overtimeHoldDays)} />
          </Box>
          <Typography sx={{ color: FAINT, fontSize: { xs: 10.5, sm: 11.5 }, lineHeight: 1.35, mt: 0.1, display: { xs: 'none', sm: 'block' } }}>Base {calc.salaryBaseDays} · Present {calc.presentDays} · Pay {calc.regularPayableDays} · Held {calc.overtimeHoldDays}</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(5, minmax(88px, 1fr))' }, columnGap: { xs: 1.2, sm: 1 }, rowGap: { xs: 0, sm: 1 }, width: { xs: '100%', lg: 560 }, px: { xs: 1, sm: 0 }, py: { xs: 0.35, sm: 0 }, bgcolor: { xs: '#fbfaf7', sm: 'transparent' }, borderTop: { xs: `1px solid ${BORDER}`, sm: 0 } }}>
          <Metric label="Per day" value={`₹${money(calc.perDayRate)}`} />
          <Metric label="Gross" value={`₹${money(calc.grossSalaryPay)}`} />
          <Metric label="TDS" value={`₹${money(calc.tdsAmount)}`} color={WARN} />
          <Metric label="Held" value={`₹${money(calc.overtimeHoldAmount)}`} color={WARN} />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}><Metric label="Payable" value={`₹${money(calc.netPayableAmount)}`} color={GREEN} /></Box>
        </Box>
      </Stack>
    </Box>)}
    {!rows.length && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 4, textAlign: 'center', color: MUTED }}>No payroll rows for this company.</Box>}
  </Stack>;
}

function TinyStat({ label, value }: { label: string; value: string }) {
  return <Box sx={{ bgcolor: PANEL, borderRadius: 0.85, px: 0.55, py: 0.35, minWidth: 0, border: '1px solid rgba(0,0,0,0.02)' }}>
    <Typography sx={{ color: FAINT, fontWeight: 900, fontSize: 7, textTransform: 'uppercase', lineHeight: 1 }}>{label}</Typography>
    <Typography sx={{ color: DARK, fontWeight: 900, fontSize: 10.5, lineHeight: 1.1 }}>{value}</Typography>
  </Box>;
}

function EmployeeMaster({ employees, canManage, letterBusyEmployeeId, onEdit, onDelete, onGenerateLetter }: { employees: PayrollEmployee[]; canManage: boolean; letterBusyEmployeeId: string; onEdit: (employee: PayrollEmployee) => void; onDelete: (employee: PayrollEmployee) => void; onGenerateLetter: (employee: PayrollEmployee) => void }) {
  return <Stack gap={1}>
    {employees.map((employee) => <Box key={employee.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#fff', p: { xs: 1.2, sm: 1.45 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.1} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: DARK, fontWeight: 900 }}>{employee.employeeName}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>{employee.companyName}{employee.mobile ? ` · ${employee.mobile}` : ''}{employee.designation ? ` · ${employee.designation}` : ''}</Typography>
        </Box>
        <Chip size="small" label={`₹${money(employee.monthlySalary)} salary`} sx={{ bgcolor: '#eef7f0', color: GREEN, fontWeight: 800 }} />
        <Chip size="small" label={employee.salaryMode === 'fixed_monthly' ? 'Fixed monthly' : `${employee.allowedHolidayDays || 0} holidays`} sx={{ bgcolor: PANEL, color: GOLD_D, fontWeight: 800 }} />
        <Chip size="small" label={`TDS ${Number(employee.tdsPercent || 0)}%`} sx={{ bgcolor: '#fff4e5', color: WARN, fontWeight: 800 }} />
        {canManage && <Stack direction="row" gap={0.5} flexWrap="wrap">
          <Button size="small" startIcon={<ArticleOutlinedIcon />} disabled={letterBusyEmployeeId === employee.id} onClick={() => onGenerateLetter(employee)}>{letterBusyEmployeeId === employee.id ? 'Generating...' : 'Joining Letter'}</Button>
          <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => onEdit(employee)}>Edit</Button>
          <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete(employee)}>Delete</Button>
        </Stack>}
      </Stack>
    </Box>)}
    {!employees.length && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 4, textAlign: 'center', color: MUTED }}>No employees for this company.</Box>}
  </Stack>;
}

function DeleteEmployeeDialog({ employee, onClose, onDelete }: { employee: PayrollEmployee; onClose: () => void; onDelete: (employee: PayrollEmployee, includeRelatedData: boolean) => Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const runDelete = async (includeRelatedData: boolean) => {
    setSaving(true);
    try { await onDelete(employee, includeRelatedData); } finally { setSaving(false); }
  };
  return <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="xs">
    <DialogTitle sx={{ fontWeight: 900 }}>Delete employee?</DialogTitle>
    <DialogContent>
      <Box sx={{ p: 1.15, bgcolor: PANEL, borderRadius: 1.5, mb: 1.2 }}>
        <Typography sx={{ fontWeight: 900 }}>{employee.employeeName}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 12 }}>{employee.companyName}</Typography>
      </Box>
      <Typography sx={{ color: MUTED, fontSize: 13 }}>Choose whether to keep or remove this employee&apos;s attendance and audit records.</Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.8 }}>
      <Button onClick={onClose} disabled={saving}>Cancel</Button>
      <Button color="error" variant="outlined" onClick={() => runDelete(false)} disabled={saving}>Employee only</Button>
      <Button color="error" variant="contained" onClick={() => runDelete(true)} disabled={saving}>Employee + all data</Button>
    </DialogActions>
  </Dialog>;
}

function JoiningLetterDialog({ employee, projectName, designationOptions, busy, onClose, onSave }: { employee: PayrollEmployee; projectName: string; designationOptions: string[]; busy: boolean; onClose: () => void; onSave: (employee: PayrollEmployee, draft: Partial<PayrollEmployee>, idProofFile: File | null, generateAfterSave: boolean) => Promise<void> }) {
  const defaultDutyTiming = '10:00 AM to 7:00 PM';
  const [draft, setDraft] = useState<Partial<PayrollEmployee>>({
    joiningDate: employee.joiningDate || new Date().toISOString().slice(0, 10),
    designation: employee.designation || '',
    dutyTiming: employee.dutyTiming || '',
    workLocation: employee.workLocation || projectName || '',
    address: employee.address || '',
    idProofType: employee.idProofType || '',
    idProofNumber: employee.idProofNumber || '',
  });
  const [idProofFile, setIdProofFile] = useState<File | null>(null);
  const update = (patch: Partial<PayrollEmployee>) => setDraft((previous) => ({ ...previous, ...patch }));
  const hasExistingProof = Boolean(employee.idProofUrl || employee.idProofPath);
  const canGenerate = Boolean(
    String(draft.joiningDate || '').trim() &&
    String(draft.designation || '').trim() &&
    String(draft.dutyTiming || '').trim() &&
    String(draft.workLocation || '').trim() &&
    String(draft.address || '').trim()
  );

  return <Dialog open onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 900 }}>Joining letter details</DialogTitle>
    <DialogContent>
      <Stack gap={1.15} sx={{ mt: 0.5 }}>
        <Box sx={{ p: 1.1, bgcolor: PANEL, borderRadius: 1.5 }}>
          <Typography sx={{ fontWeight: 900 }}>{employee.employeeName}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>{employee.companyName} · ₹{money(employee.monthlySalary)} salary</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField label="Joining date" type="date" value={draft.joiningDate || ''} onChange={(event) => update({ joiningDate: event.target.value })} InputLabelProps={{ shrink: true }} fullWidth required />
          <Autocomplete
            freeSolo
            options={designationOptions}
            value={String(draft.designation || '')}
            onChange={(_, value) => update({ designation: String(value || '') })}
            onInputChange={(_, value) => update({ designation: String(value || '') })}
            fullWidth
            renderInput={(params) => <TextField {...params} label="Designation" required helperText="Select existing or type a new designation." />}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <TextField label="Duty timing" value={draft.dutyTiming || ''} onChange={(event) => update({ dutyTiming: event.target.value })} placeholder={defaultDutyTiming} fullWidth required />
          <Button variant="outlined" onClick={() => update({ dutyTiming: defaultDutyTiming })} disabled={busy} sx={{ minHeight: 54, minWidth: { sm: 190 }, whiteSpace: 'nowrap' }}>
            {defaultDutyTiming}
          </Button>
        </Stack>
        <TextField label="Work location" value={draft.workLocation || ''} onChange={(event) => update({ workLocation: event.target.value })} fullWidth required />
        <TextField label="Address" value={draft.address || ''} onChange={(event) => update({ address: event.target.value })} multiline minRows={2} fullWidth required />
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField select label="ID proof type" value={draft.idProofType || ''} onChange={(event) => update({ idProofType: event.target.value })} fullWidth>
            <MenuItem value="">Not selected</MenuItem>
            <MenuItem value="Aadhaar Card">Aadhaar Card</MenuItem>
            <MenuItem value="PAN Card">PAN Card</MenuItem>
            <MenuItem value="Driving Licence">Driving Licence</MenuItem>
            <MenuItem value="Voter ID">Voter ID</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField label="ID proof number" value={draft.idProofNumber || ''} onChange={(event) => update({ idProofNumber: event.target.value })} fullWidth />
        </Stack>
        <Button variant="outlined" component="label" disabled={busy}>
          {idProofFile ? idProofFile.name : hasExistingProof ? `Uploaded: ${employee.idProofFileName || 'ID proof'}` : 'Upload ID proof (optional)'}
          <input hidden type="file" accept="image/*,application/pdf" onChange={(event) => setIdProofFile(event.target.files?.[0] || null)} />
        </Button>
        {!canGenerate && <Alert severity="info">Fill joining date, designation, duty timing, work location, and address before generating the letter. ID proof upload is optional.</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.8 }}>
      <Button onClick={onClose} disabled={busy}>Cancel</Button>
      <Button variant="outlined" onClick={() => onSave(employee, draft, idProofFile, false)} disabled={busy}>{busy ? 'Saving...' : 'Save Details'}</Button>
      <Button variant="contained" onClick={() => onSave(employee, draft, idProofFile, true)} disabled={busy || !canGenerate}>{busy ? 'Generating...' : 'Save & Generate Letter'}</Button>
    </DialogActions>
  </Dialog>;
}

function EmployeeDialog({ employee, companyProfiles, onClose, onSave }: { employee: PayrollEmployee; companyProfiles: CompanyProfile[]; onClose: () => void; onSave: (employee: PayrollEmployee) => Promise<void> }) {
  const [draft, setDraft] = useState(employee);
  const [saving, setSaving] = useState(false);
  const update = (patch: Partial<PayrollEmployee>) => setDraft((previous) => ({ ...previous, ...patch }));
  const canSave = draft.employeeName.trim() && draft.companyProfileId && draft.monthlySalary > 0;
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try { await onSave(draft); } finally { setSaving(false); }
  };
  return <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 900 }}>{draft.id ? 'Edit employee' : 'Add employee'}</DialogTitle>
    <DialogContent>
      <Stack gap={1.2} sx={{ mt: 0.5 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Company</InputLabel>
          <Select label="Company" value={draft.companyProfileId} onChange={(event) => {
            const company = companyProfiles.find((item) => item.id === event.target.value);
            update({ companyProfileId: event.target.value, companyName: company?.name || '' });
          }}>
            {companyProfiles.map((company) => <MenuItem key={company.id} value={company.id}>{company.name || company.id}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Employee name" value={draft.employeeName} onChange={(event) => update({ employeeName: event.target.value })} fullWidth />
        <TextField label="Mobile" value={draft.mobile || ''} onChange={(event) => update({ mobile: event.target.value })} fullWidth />
        <FormControl fullWidth size="small">
          <InputLabel>Salary calculation</InputLabel>
          <Select label="Salary calculation" value={draft.salaryMode || 'attendance_based'} onChange={(event) => update({ salaryMode: event.target.value as SalaryMode })}>
            <MenuItem value="attendance_based">Attendance based salary</MenuItem>
            <MenuItem value="fixed_monthly">Fixed monthly salary</MenuItem>
          </Select>
        </FormControl>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField label="Monthly salary" type="number" value={draft.monthlySalary || ''} onChange={(event) => update({ monthlySalary: Math.round(numberValue(event.target.value)) })} inputProps={{ min: 0 }} fullWidth />
          <TextField label="Allowed holidays" type="number" value={draft.allowedHolidayDays || ''} onChange={(event) => update({ allowedHolidayDays: Math.round(numberValue(event.target.value)) })} inputProps={{ min: 0, max: 10 }} disabled={(draft.salaryMode || 'attendance_based') === 'fixed_monthly'} fullWidth />
          <TextField label="TDS %" type="number" value={draft.tdsPercent || ''} onChange={(event) => update({ tdsPercent: numberValue(event.target.value) })} inputProps={{ min: 0, max: 100, step: 0.01 }} fullWidth />
        </Stack>
        <ToggleButtonGroup exclusive size="small" value={draft.active === false ? 'inactive' : 'active'} onChange={(_, value) => value && update({ active: value === 'active' })} sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800 } }}>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>
        <TextField label="Notes" value={draft.notes || ''} onChange={(event) => update({ notes: event.target.value })} multiline minRows={2} fullWidth />
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={onClose} disabled={saving}>Cancel</Button><Button variant="contained" onClick={save} disabled={!canSave || saving}>{saving ? 'Saving...' : 'Save employee'}</Button></DialogActions>
  </Dialog>;
}
